import React, { useMemo } from 'react';
import {
  Row,
  Col,
  Select,
  Tooltip,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import CompanySettingsCard from '../CompanySettingsCard';
import Colors from '../../constants/Colors';
import HoverHelp from '../../common/HoverHelp';
import { simpleTextBasedFieldTypes } from '../../forms/formHelpers';

const getDownstreamBucketTemplates = ({
  bucketTemplateId,
  bucketTemplateMap,
  defaultReturn,
}) => {
  const bucketTemplate = bucketTemplateMap[bucketTemplateId];
  if (!bucketTemplate) return defaultReturn;

  const options = [];

  bucketTemplate.downstreamTypes?.map((type) => type.split('-')?.[0]).forEach((id) => {
    const relevantTemplate = bucketTemplateMap[id];
    if (!relevantTemplate) return;
    options.push(relevantTemplate);
  });

  return options;
};

const getUpstreamBucketTemplates = ({
  bucketTemplateId,
  bucketTemplateMap,
  defaultReturn,
}) => {
  const bucketTemplate = bucketTemplateMap[bucketTemplateId];
  if (!bucketTemplate) return defaultReturn;
  const options = [];
  bucketTemplate.upstreamTypes?.map((type) => type.split('-')?.[0]).forEach((id) => {
    const relevantTemplate = bucketTemplateMap[id];
    if (!relevantTemplate) return;
    options.push(relevantTemplate);
  });
  return options;
};

const WarningComponent = () => (
  <Tooltip
    title="This item is not linked to a valid bucket template."
    placement="top"
  >
    <ExclamationCircleOutlined
      style={{ color: Colors.ONTRACCR_RED, marginLeft: 5, fontSize: 16 }}
    />
  </Tooltip>
);

export default function ABWorkReportingBucketMapping({
  itemId,
  segmentId,
  bidItemId,
  authorizedQuantityField,
  contractField,
  dateField,
  uomField,
  jobCodeField,
  inspectorField,
  bucketOptions,
  bucketTemplateMap,
  saveCompanySettings,
  abWorkReportingMapping,
  disabled = false,
}) {
  const {
    itemOptions,
    segmentOptions,
    bidItemOptions,
  } = useMemo(() => {
    const defaultResult = {
      itemOptions: bucketOptions,
      segmentOptions: bucketOptions,
      bidItemOptions: bucketOptions,
    };

    if (!itemId && !segmentId && !bidItemId) {
      return defaultResult;
    }

    if (itemId) {
      const segmentOpts = getDownstreamBucketTemplates({
        bucketTemplateId: itemId,
        bucketTemplateMap,
        defaultReturn: bucketOptions,
      });

      const bidItemOpts = segmentId
        ? getDownstreamBucketTemplates({
          bucketTemplateId: segmentId,
          bucketTemplateMap,
          defaultReturn: bucketOptions,
        })
        : segmentOpts.map((segment) => getDownstreamBucketTemplates({
          bucketTemplateId: segment.id,
          bucketTemplateMap,
          defaultReturn: bucketOptions,
        })).flat();

      return {
        itemOptions: bucketOptions,
        segmentOptions: segmentOpts,
        bidItemOptions: bidItemOpts,
      };
    }

    if (segmentId) {
      const itemOpts = getUpstreamBucketTemplates({
        bucketTemplateId: segmentId,
        bucketTemplateMap,
        defaultReturn: bucketOptions,
      });

      const bidItemOpts = getDownstreamBucketTemplates({
        bucketTemplateId: segmentId,
        bucketTemplateMap,
        defaultReturn: bucketOptions,
      });

      return {
        itemOptions: itemOpts,
        segmentOptions: bucketOptions,
        bidItemOptions: bidItemOpts,
      };
    }

    const segmentOpts = getUpstreamBucketTemplates({
      bucketTemplateId: bidItemId,
      bucketTemplateMap,
      defaultReturn: bucketOptions,
    });

    const itemOpts = segmentOpts.map((segment) => getUpstreamBucketTemplates({
      bucketTemplateId: segment.id,
      bucketTemplateMap,
      defaultReturn: bucketOptions,
    })).flat();

    return {
      itemOptions: bucketOptions,
      segmentOptions: segmentOpts,
      bidItemOptions: itemOpts,
    };
  }, [itemId, segmentId, bidItemId, bucketOptions, bucketTemplateMap]);

  const simpleTextBaseFields = [];
  const itemTextBasedFields = [];
  const dateFields = [];
  const itemTemplate = bucketTemplateMap[itemId];
  const bidItemTemplate = bucketTemplateMap[bidItemId];

  if (bidItemTemplate) {
    bidItemTemplate.customFields?.fields?.forEach((field) => {
      if (simpleTextBasedFieldTypes.has(field.type)) {
        simpleTextBaseFields.push({
          label: field.title,
          value: field.id,
        });
      }

      if (field.type === 'dateTime') {
        dateFields.push({
          label: field.title,
          value: field.id,
        });
      }
    });
  }

  if (itemTemplate) {
    itemTemplate.customFields?.fields?.forEach((field) => {
      if (simpleTextBasedFieldTypes.has(field.type)) {
        itemTextBasedFields.push({
          label: field.title,
          value: field.id,
        });
      }
    });
  }

  const hasItemError = itemId && !itemOptions.some((option) => option.value === itemId);
  const hasSegmentError = segmentId && !segmentOptions.some((option) => option.value === segmentId);
  const hasBidItemError = bidItemId && !bidItemOptions.some((option) => option.value === bidItemId);

  return (
    <CompanySettingsCard
      title="Bucket To Report Mapping"
      leftHeaderView={(
        <HoverHelp
          content="This is used to map the bucket into the work report. Please note that the item must be a parent of the segment and the segment must be a parent of the bid item."
          placement="top"
          iconProps={{ style: { fontSize: 16 } }}
        />
      )}
    >
      <Row style={{ marginBottom: 10, marginTop: 10 }}>
        <Col span={6}>
          Item
          {hasItemError && (
            <WarningComponent />
          )}
        </Col>
        <Col span={18}>
          <Select
            options={itemOptions}
            value={itemId}
            onChange={(value) => saveCompanySettings({
              abWorkReportingMapping: { ...abWorkReportingMapping, itemId: value },
            })}
            style={{ width: '100%' }}
            allowClear
            disabled={disabled}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 10 }}>
        <Col span={6}>
          Segment
          {hasSegmentError && (
            <WarningComponent />
          )}
        </Col>
        <Col span={18}>
          <Select
            options={segmentOptions}
            value={segmentId}
            onChange={(value) => saveCompanySettings({
              abWorkReportingMapping: { ...abWorkReportingMapping, segmentId: value },
            })}
            style={{ width: '100%' }}
            allowClear
            disabled={disabled}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 10 }}>
        <Col span={6}>
          Bid Item
          {hasBidItemError && (
            <WarningComponent />
          )}
        </Col>
        <Col span={18}>
          <Select
            options={bidItemOptions}
            value={bidItemId}
            onChange={(value) => saveCompanySettings({
              abWorkReportingMapping: { ...abWorkReportingMapping, bidItemId: value },
            })}
            style={{ width: '100%' }}
            allowClear
            disabled={disabled}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 10 }}>
        <Col span={6}>
          Authorized Quantity Field
          <HoverHelp
            content="This is the field that will be used when determining the authorized quantity for export.
            This field should be a custom field on the bid item."
            iconProps={{
              style: { marginLeft: 5 }
            }}
          />
        </Col>
        <Col span={18}>
          <Select
            options={simpleTextBaseFields}
            value={authorizedQuantityField}
            onChange={(value) => saveCompanySettings({
              abWorkReportingMapping: { ...abWorkReportingMapping, authorizedQuantityField: value },
            })}
            style={{ width: '100%' }}
            disabled={disabled}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 10 }}>
        <Col span={6}>
          Contract Field
          <HoverHelp
            content="This is the field that will be used when determining the contract for export.
            This field should be a custom field on the bid item."
            iconProps={{
              style: { marginLeft: 5 }
            }}
          />
        </Col>
        <Col span={18}>
          <Select
            options={simpleTextBaseFields}
            value={contractField}
            onChange={(value) => saveCompanySettings({
              abWorkReportingMapping: { ...abWorkReportingMapping, contractField: value },
            })}
            style={{ width: '100%' }}
            disabled={disabled}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 10 }}>
        <Col span={6}>
          Date Field
          <HoverHelp
            content="This is the field that will be used when determining the fiscal date for export.
            This field should be a custom field on the bid item."
            iconProps={{
              style: { marginLeft: 5 }
            }}
          />
        </Col>
        <Col span={18}>
          <Select
            options={dateFields}
            value={dateField}
            onChange={(value) => saveCompanySettings({
              abWorkReportingMapping: { ...abWorkReportingMapping, dateField: value },
            })}
            style={{ width: '100%' }}
            disabled={disabled}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 10 }}>
        <Col span={6}>
          UoM Field
          <HoverHelp
            content="This is the field that will be used when determining the UoM for export.
            This field should be a custom field on the bid item."
            iconProps={{
              style: { marginLeft: 5 }
            }}
          />
        </Col>
        <Col span={18}>
          <Select
            options={simpleTextBaseFields}
            value={uomField}
            onChange={(value) => saveCompanySettings({
              abWorkReportingMapping: { ...abWorkReportingMapping, uomField: value },
            })}
            style={{ width: '100%' }}
            disabled={disabled}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 10 }}>
        <Col span={6}>
          Job Code Field
          <HoverHelp
            content="This is the field that will be used when determining the job code for the work report.
            This field should be a custom field on the bid item."
            iconProps={{
              style: { marginLeft: 5 }
            }}
          />
        </Col>
        <Col span={18}>
          <Select
            options={simpleTextBaseFields}
            value={jobCodeField}
            onChange={(value) => saveCompanySettings({
              abWorkReportingMapping: { ...abWorkReportingMapping, jobCodeField: value },
            })}
            style={{ width: '100%' }}
            disabled={disabled}
          />
        </Col>
      </Row>
      <Row>
        <Col span={6}>
          Inspector Field
          <HoverHelp
            content="This is the field that will be used when determining the inspector for the export.
            This field should be a custom field on the bid item."
            iconProps={{
              style: { marginLeft: 5 }
            }}
          />
        </Col>
        <Col span={18}>
          <Select
            options={simpleTextBaseFields}
            value={inspectorField}
            onChange={(value) => saveCompanySettings({
              abWorkReportingMapping: { ...abWorkReportingMapping, inspectorField: value },
            })}
            style={{ width: '100%' }}
            disabled={disabled}
          />
        </Col>
      </Row>
    </CompanySettingsCard>
  );
}

ABWorkReportingBucketMapping.propTypes = {
  itemId: PropTypes.string,
  segmentId: PropTypes.string,
  bidItemId: PropTypes.string,
  authorizedQuantityField: PropTypes.string,
  contractField: PropTypes.string,
  dateField: PropTypes.string,
  uomField: PropTypes.string,
  jobCodeField: PropTypes.string,
  inspectorField: PropTypes.string,
  bucketOptions: PropTypes.array,
  bucketTemplateMap: PropTypes.object,
  saveCompanySettings: PropTypes.func.isRequired,
  abWorkReportingMapping: PropTypes.object,
  disabled: PropTypes.bool,
};

ABWorkReportingBucketMapping.defaultProps = {
  itemId: null,
  segmentId: null,
  bidItemId: null,
  authorizedQuantityField: null,
  contractField: null,
  dateField: null,
  uomField: null,
  jobCodeField: null,
  inspectorField: null,
  bucketOptions: [],
  bucketTemplateMap: {},
  abWorkReportingMapping: {},
  disabled: false,
};